import { inject, provide, ref, shallowRef, type InjectionKey, type Ref } from "vue";
import { readInheritanceInitialValue } from "@/v2/kits/tenant-inheritance/utils/readInitialValue";
import { injectGridContext } from "@/v2/framework/grid/composables/grid-context-key";
import type {
  DialogConfig,
  DialogContentNode,
  TextField,
  SelectField,
  ArrayField,
  TranslationSubmitGate,
  GridSubmitGate,
} from "@/v2/framework/dialog/types";
import { isTranslationsTab, isGridTab } from "@/v2/framework/dialog/types";
import type { RegistryLeafField } from "./dialog-leaf-validation";
import {
  validateField,
  validateNonStructuredField,
} from "./dialog-leaf-validation";
import {
  collectStructuredArrayErrors,
  normalizeStructuredArrayValue,
  replaceErrorsUnderStructuredBase,
} from "./structured-array-field";

type LeafField = RegistryLeafField;

/** Set by `StepperStructure` while mounted; drives shared `DialogFooter` actions. */
export type DialogStepperNav = {
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  back: () => void;
  /** Validates the current step (if declarative) then submits. */
  submit: () => void;
};

export interface DialogContext {
  values: Ref<Record<string, unknown>>;
  /** Snapshot of `initialValues` at open — used for dirty detection (e.g. tenant inheritance). */
  baseline: Ref<Record<string, unknown>>;
  errors: Ref<Record<string, string | undefined>>;
  setValue: (path: string, value: unknown) => void;
  getValue: (path: string) => unknown;
  /**
   * Runs required + validate checks for every leaf field in `nodes`.
   * Populates errors. Returns true only if all pass.
   */
  validateAll: (nodes: DialogContentNode[]) => boolean;
  isSubmitting: Ref<boolean>;
  row: Record<string, unknown> | undefined;
  submit: () => Promise<void>;
  close: () => void;
  stepperNav: Ref<DialogStepperNav | null>;
  /**
   * Single slot for the active translations surface. Call `populate` on mount and
   * `clear` on unmount (pass the same gate object so another tab cannot wipe a newer slot).
   */
  translationSubmit: {
    populate: (gate: TranslationSubmitGate) => void;
    clear: (gate: TranslationSubmitGate) => void;
  };
  gridSubmit: {
    populate: (gate: GridSubmitGate) => void;
    clear: (gate: GridSubmitGate) => void;
  };
  /** Active embedded grid tab submit gate, if the tab was mounted at least once. */
  getGridSubmitGate: () => GridSubmitGate | null;
}

export const DIALOG_CONTEXT_KEY: InjectionKey<DialogContext> = Symbol("dialogContext");

// ── Dot-path utilities ────────────────────────────────────────────────

export const setAtPath = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const parts = path.split(".");
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    if (typeof current[key] !== "object" || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]!] = value;
}

export const getAtPath = (obj: Record<string, unknown>, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined,
      obj,
    );

/** Overlay form paths from optional field bindings (see `DialogFieldInitialBinding`). */
function applyDialogFieldInitialBindings(
  values: Record<string, unknown>,
  raw: Record<string, unknown>,
  registry: Map<string, LeafField>,
): void {
  for (const [path, field] of registry) {
    const bound = readLeafFromRow(raw, field, path);
    if (bound === undefined) continue;
    setAtPath(values, path, initialValueForLeaf(field, bound));
  }
}

// ── Field registry (path → leaf field config) ─────────────────────────

export const buildFieldRegistry = (
  nodes: DialogContentNode[],
  prefix = "",
): Map<string, LeafField> => {
  const map = new Map<string, LeafField>();
  for (const node of nodes) {
    if (node.type === "component") continue;
    if (node.type === "group") {
      const p = prefix ? `${prefix}.${node.name}` : node.name;
      for (const [k, v] of buildFieldRegistry(node.children, p)) {
        map.set(k, v);
      }
    } else {
      const p = prefix ? `${prefix}.${node.name}` : node.name;
      map.set(p, node as LeafField);
    }
  }
  return map;
}

function readLeafFromRow(row: unknown, field: LeafField, path?: string): unknown {
  const fmt = field as LeafField & {
    valueFormatter?: (row: unknown) => unknown;
    inheritance?: import("@/v2/framework/dialog/types").TenantInheritanceFieldConfig;
  };

  if (typeof fmt.valueFormatter === "function") return fmt.valueFormatter(row);

  if (fmt.inheritance && path) {
    return readInheritanceInitialValue(row, path, field.name, fmt.inheritance);
  }

  return undefined;
}

function initialValueForLeaf(field: LeafField, raw: unknown): unknown {
  if (field.type === "array-field") return Array.isArray(raw) ? raw : [];
  if (field.type === "structured-array-field") return normalizeStructuredArrayValue(raw, field);
  if (field.type === "select-field") return raw ?? field.defaultValue ?? null;
  if (raw === null || raw === undefined) return "";
  return String(raw);
}

/**
 * Nested `values` object for one backing row (e.g. per-language translation), using the same paths as `buildFieldRegistry`.
 */
export const nestedValuesFromRow = (
  row: unknown,
  registry: Map<string, LeafField>,
): Record<string, unknown> => {
  const nested: Record<string, unknown> = {};
  for (const [path, field] of registry) {
    setAtPath(nested, path, initialValueForLeaf(field, readLeafFromRow(row, field, path)));
  }
  return nested;
}

/**
 * Read-only column for child tenants: parent tenant strings for the same language row
 * (`parentName`, `parentInfo`, `supplementalNames[].parentName`, …).
 */
function readParentReferenceLeafFromRow(row: unknown, field: LeafField): unknown {
  if (!row || typeof row !== "object") return undefined;
  const r = row as Record<string, unknown>;

  if (field.name === "name") {
    return r.parentName;
  }
  if (field.name === "info") {
    return r.parentInfo;
  }

  const supplementalMatch = /^supplementalName_(.+)$/.exec(field.name);
  if (supplementalMatch) {
    const typeId = supplementalMatch[1]!;
    const list = r.supplementalNames;
    if (!Array.isArray(list)) return undefined;
    const entry = list.find(
      (s: unknown) =>
        s &&
        typeof s === "object" &&
        String((s as Record<string, unknown>).supplementalNameTypeId) === String(typeId),
    ) as Record<string, unknown> | undefined;
    return entry?.parentName;
  }

  return undefined;
}

export const nestedParentReferenceValuesFromRow = (
  row: unknown,
  registry: Map<string, LeafField>,
): Record<string, unknown> => {
  const nested: Record<string, unknown> = {};
  for (const [path, field] of registry) {
    const raw = readParentReferenceLeafFromRow(row, field);
    setAtPath(nested, path, initialValueForLeaf(field, raw));
  }
  return nested;
}

function getStructureNodes(structure: DialogConfig["structure"]): DialogContentNode[] {
  if (structure.type === "component") return [];
  if (structure.type === "tabbed") {
    return structure.tabs.flatMap((tab) => {
      if (isTranslationsTab(tab)) return [];
      if (isGridTab(tab)) return [];
      if ("children" in tab) return tab.children;
      return [];
    });
  }
  if (structure.type === "flat") {
    return structure.children;
  }
  return structure.steps.flatMap((step) => ("children" in step ? step.children : []));
}

// Re-export validation API (implementation in `dialog-leaf-validation.ts`)
export { validateField } from "./dialog-leaf-validation";

// ── Context factory ───────────────────────────────────────────────────

export const createDialogContext = (
  config: DialogConfig,
  row: Record<string, unknown> | undefined,
  initialValues: Record<string, unknown>,
  onClose: () => void,
  onRefetch: () => void,
): DialogContext => {
  const safeInitial =
    initialValues && typeof initialValues === "object" && !Array.isArray(initialValues)
      ? initialValues
      : {};
  const rawInitial = JSON.parse(JSON.stringify(safeInitial)) as Record<string, unknown>;
  const baseline = ref<Record<string, unknown>>(JSON.parse(JSON.stringify(safeInitial)));
  const values = ref<Record<string, unknown>>(JSON.parse(JSON.stringify(safeInitial)));
  const errors = ref<Record<string, string | undefined>>({});
  const isSubmitting = ref(false);
  const stepperNav = shallowRef<DialogStepperNav | null>(null);
  const translationSubmitGate = shallowRef<TranslationSubmitGate | null>(null);
  const gridSubmitGate = shallowRef<GridSubmitGate | null>(null);
  const parentGrid = injectGridContext();

  const fieldRegistry = buildFieldRegistry(getStructureNodes(config.structure));
  applyDialogFieldInitialBindings(values.value, rawInitial, fieldRegistry);

  const setValue = (path: string, value: unknown): void => {
    setAtPath(values.value, path, value);

    const structuredAtPath = fieldRegistry.get(path);
    if (structuredAtPath?.type === "structured-array-field") {
      const bucket = collectStructuredArrayErrors(
        path,
        structuredAtPath,
        value,
        values.value,
        validateNonStructuredField,
      );
      errors.value = replaceErrorsUnderStructuredBase(errors.value, path, bucket);
      return;
    }

    const field = fieldRegistry.get(path);
    if (!field) return;
    const error = validateField(field, value, values.value);
    errors.value = Object.assign({}, errors.value, { [path]: error });
  };

  const getValue = (path: string): unknown => getAtPath(values.value, path);

  const validateAll = (nodes: DialogContentNode[]): boolean => {
    const registry = buildFieldRegistry(nodes);
    let valid = true;
    let newErrors = Object.assign({}, errors.value);

    for (const [path, field] of registry) {
      if (field.type === "structured-array-field") {
        const value = getValue(path);
        const bucket = collectStructuredArrayErrors(
          path,
          field,
          value,
          values.value,
          validateNonStructuredField,
        );
        for (const msg of Object.values(bucket)) {
          if (msg) valid = false;
        }
        newErrors = replaceErrorsUnderStructuredBase(newErrors, path, bucket);
        continue;
      }

      const value = getValue(path);
      const error = validateField(field, value, values.value);
      newErrors[path] = error;
      if (error) valid = false;
    }

    errors.value = newErrors;
    return valid;
  };

  const translationSubmit = {
    populate(gate: TranslationSubmitGate): void {
      translationSubmitGate.value = gate;
    },
    clear(gate: TranslationSubmitGate): void {
      if (translationSubmitGate.value === gate) {
        translationSubmitGate.value = null;
      }
    },
  };

  const gridSubmit = {
    populate(gate: GridSubmitGate): void {
      gridSubmitGate.value = gate;
    },
    clear(gate: GridSubmitGate): void {
      if (gridSubmitGate.value === gate) {
        gridSubmitGate.value = null;
      }
    },
  };

  const submit = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      const submitParams = Object.assign(
        {
          row,
          getGridSubmitGate: () => gridSubmitGate.value,
        },
        parentGrid ? { deleteGridRow: (id: unknown) => parentGrid.delete(id) } : {},
      );
      const result = await config.api.submit(values.value, submitParams);
      config.onSuccess?.(result);
      onClose();
      if (config.refetchAfterSubmit !== false) {
        onRefetch();
      }
    } catch (err) {
      config.onError?.(err);
    } finally {
      isSubmitting.value = false;
    }
  };

  const context: DialogContext = {
    values,
    baseline,
    errors,
    setValue,
    getValue,
    validateAll,
    isSubmitting,
    row,
    submit,
    close: onClose,
    stepperNav,
    translationSubmit,
    gridSubmit,
    getGridSubmitGate: () => gridSubmitGate.value,
  };

  provide(DIALOG_CONTEXT_KEY, context);
  return context;
}

export const useDialogContext = (): DialogContext => {
  const ctx = inject(DIALOG_CONTEXT_KEY);
  if (!ctx) throw new Error("[useDialogContext] must be used inside DialogHost");
  return ctx;
}
